import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { User, Info, Phone, ArrowLeftIcon, PenIcon, Trash2Icon, CalendarClock, FileText } from "lucide-react";
import { decryptToken } from "../utils/getToken";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import Loader from "../Loader";
import { admissionStatusLabel, formatAdmissionDateIST, formatAdmissionDateTimeIST } from "./admissionFormat";
import { admissionAssetUrl, isPdfPath } from "./admissionFormUpload";

const AdmissionDetail = () => {
    const { id } = useParams();
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [linkedInvoiceId, setLinkedInvoiceId] = useState(null);
    const navigate = useNavigate();
    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");

    useEffect(() => {
        const fetchRow = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${url}/admission/record/${id}`, {
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                });
                const result = await res.json();
                if (!res.ok) {
                    throw new Error(result.message || "Failed to load admission");
                }
                setRow(result.user);
            } catch (error) {
                console.error("Error loading admission:", error);
                setRow(null);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchRow();
    }, [id, url, token]);

    useEffect(() => {
        setLinkedInvoiceId(null);
        if (!row?.admission_id || !isLoggedIn?.main_id || !token) return undefined;

        let cancelled = false;
        const params = new URLSearchParams({
            user_id: String(isLoggedIn.main_id),
            invoice_number: row.admission_id,
        });

        fetch(`${url}/newinvoice/by-invoice-number?${params.toString()}`, {
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        })
            .then(async (res) => {
                if (!res.ok) return null;
                return res.json();
            })
            .then((inv) => {
                if (!cancelled && inv?.id != null) setLinkedInvoiceId(inv.id);
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [row?.id, row?.admission_id, isLoggedIn?.main_id, token, url]);

    if (loading) return <Loader />;
    if (!row) {
        return (
            <div className="ecom-customer-detail-container" style={{ padding: "24px", textAlign: "center" }}>
                <p style={{ marginBottom: "16px", color: "#666" }}>Admission not found.</p>
                <button type="button" className="oms-add-btn" onClick={() => navigate("/admission")}>
                    Back to admissions
                </button>
            </div>
        );
    }

    const handleEdit = () => {
        navigate("/edit-admission", { state: row });
    };

    const handleDelete = async (pk) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This admission will be permanently deleted.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${url}/admission/${pk}`, {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                    });

                    if (!response.ok) {
                        const err = await response.json();
                        throw new Error(err?.message || "Failed to delete");
                    }

                    Swal.fire("Deleted!", "Admission has been deleted.", "success");
                    navigate("/admission");
                } catch (error) {
                    console.error("Delete error:", error.message);
                    Swal.fire("Error!", `Failed to delete. ${error.message}`, "error");
                }
            }
        });
    };

    return (
        <div>
            <div className="ecom-customer-detail-container">
                <div className="ecom-customer-header-detail">
                    <div className="ecom-customer-header-left-container">
                        <ArrowLeftIcon
                            role="button"
                            tabIndex={0}
                            aria-label="Back to admissions"
                            onClick={() => navigate("/admission")}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    navigate("/admission");
                                }
                            }}
                            style={{ cursor: "pointer" }}
                        />
                        <h2 className="ecom-customer-name-highlight">{row.name || "Admission"}</h2>
                    </div>
                    <div className="ecom-customer-header-right-container">
                        <PenIcon
                            role="button"
                            aria-label="Edit admission"
                            tabIndex={0}
                            style={{ cursor: "pointer" }}
                            onClick={handleEdit}
                        />
                        <Trash2Icon
                            role="button"
                            aria-label="Delete admission"
                            tabIndex={0}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDelete(row.id)}
                        />
                    </div>
                </div>

                <section className="ecom-customer-section-box ecom-customer-details">
                    <h4>
                        <User size={16} style={{ marginRight: "8px" }} /> Personal
                    </h4>
                    <div className="ecom-customer-detail-grid">
                        {row.studentImage && (
                            <div style={{ gridColumn: "1 / -1" }}>
                                <label>Student photo:</label>
                                <div style={{ marginTop: 8 }}>
                                    <img
                                        src={admissionAssetUrl(url, row.studentImage)}
                                        alt=""
                                        style={{ maxWidth: 160, maxHeight: 160, borderRadius: 12, objectFit: "cover" }}
                                    />
                                </div>
                            </div>
                        )}
                        <div><label>Name:</label><span>{row.name || "—"}</span></div>
                        <div><label>Father:</label><span>{row.fatherName || "—"}</span></div>
                        <div><label>Mother:</label><span>{row.motherName || "—"}</span></div>
                        <div><label>DOB:</label><span>{formatAdmissionDateIST(row.dob)}</span></div>
                        <div><label>Gender:</label><span>{row.gender || "—"}</span></div>
                        <div><label>School:</label><span>{row.school || "—"}</span></div>
                        <div style={{ gridColumn: "1 / -1" }}>
                            <label>Aadhaar:</label>
                            <span>
                                {row.aadharImage ? (
                                    isPdfPath(row.aadharImage) ? (
                                        <a
                                            href={admissionAssetUrl(url, row.aadharImage)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View document (PDF)
                                        </a>
                                    ) : (
                                        <>
                                            <a
                                                href={admissionAssetUrl(url, row.aadharImage)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ display: "block", marginBottom: 8 }}
                                            >
                                                Open full size
                                            </a>
                                            <img
                                                src={admissionAssetUrl(url, row.aadharImage)}
                                                alt=""
                                                style={{ maxWidth: 240, borderRadius: 8 }}
                                            />
                                        </>
                                    )
                                ) : row.aadhar ? (
                                    row.aadhar
                                ) : (
                                    "—"
                                )}
                            </span>
                        </div>
                    </div>
                </section>

                <section className="ecom-customer-section-box ecom-customer-details">
                    <h4>
                        <Info size={16} style={{ marginRight: "8px" }} /> Enrollment
                    </h4>
                    <div className="ecom-customer-detail-grid">
                        <div><label>Admission ID:</label><span>{row.admission_id || "—"}</span></div>
                        <div><label>Payment ID:</label><span>{row.payment_id || "—"}</span></div>
                        <div><label>Status:</label><span>{admissionStatusLabel(row.status)}</span></div>
                        <div><label>Center:</label><span>{row.center || "—"}</span></div>
                        {row.batch && (
                            <div style={{ gridColumn: "1 / -1" }}>
                                <label>Batch:</label>
                                <span>
                                    {row.batch.name}
                                    {row.batch.start_time && row.batch.end_time
                                        ? ` · ${row.batch.start_time}–${row.batch.end_time}`
                                        : ""}
                                    {Array.isArray(row.batch.days) && row.batch.days.length
                                        ? ` · ${row.batch.days.join(", ")}`
                                        : ""}
                                </span>
                            </div>
                        )}
                        <div><label>Address:</label><span>{row.address || "—"}</span></div>
                        <div><label>Pincode:</label><span>{row.pincode || "—"}</span></div>
                        <div>
                            <label>Rules agreed:</label>
                            <span>
                                {row.rulesAgreed === "1"
                                    ? "Yes"
                                    : row.rulesAgreed === "0"
                                      ? "No"
                                      : row.rulesAgreed || "—"}
                            </span>
                        </div>
                        {linkedInvoiceId != null && (
                            <div style={{ gridColumn: "1 / -1" }}>
                                <label>Admission fee invoice:</label>
                                <div style={{ marginTop: "8px" }}>
                                    <button
                                        type="button"
                                        className="oms-add-btn"
                                        onClick={() => navigate(`/invoice-detail/${linkedInvoiceId}`)}
                                    >
                                        <FileText size={16} style={{ marginRight: "8px", verticalAlign: "middle" }} />
                                        Open invoice
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                <section className="ecom-customer-section-box ecom-customer-details">
                    <h4>
                        <Phone size={16} style={{ marginRight: "8px" }} /> Contact
                    </h4>
                    <div className="ecom-customer-detail-grid">
                        <div><label>Email:</label><span>{row.email || "—"}</span></div>
                        <div><label>Mobile:</label><span>{row.mobile || "—"}</span></div>
                    </div>
                </section>

                {(() => {
                    let custom = {};
                    try {
                        custom = row.custom_fields_json
                            ? (typeof row.custom_fields_json === "string"
                                ? JSON.parse(row.custom_fields_json)
                                : row.custom_fields_json)
                            : {};
                    } catch { custom = {}; }
                    const entries = Object.entries(custom || {}).filter(([, v]) => v != null && v !== "");
                    if (!entries.length) return null;
                    return (
                        <section className="ecom-customer-section-box ecom-customer-details">
                            <h4>
                                <Info size={16} style={{ marginRight: "8px" }} /> Custom fields
                            </h4>
                            <div className="ecom-customer-detail-grid">
                                {entries.map(([key, val]) => (
                                    <div key={key}>
                                        <label>{key.replace(/^custom_/, "").replace(/_/g, " ")}:</label>
                                        <span>{String(val)}</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    );
                })()}

                <section className="ecom-customer-section-box ecom-customer-details">
                    <h4>
                        <CalendarClock size={16} style={{ marginRight: "8px" }} /> Record
                    </h4>
                    <div className="ecom-customer-detail-grid">
                        <div>
                            <label>Created:</label>
                            <span>{formatAdmissionDateTimeIST(row.createdAt)}</span>
                        </div>
                        <div>
                            <label>Last updated:</label>
                            <span>{formatAdmissionDateTimeIST(row.updatedAt)}</span>
                        </div>
                        <div>
                            <label>Added by:</label>
                            <span>{row.user_name || "—"}</span>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default AdmissionDetail;
